import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Billing.css';

const Billing = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  // Mock subscription data
  const [subscription] = useState({
    plan: 'Professional',
    status: 'Active',
    billingCycle: 'Monthly',
    nextBillingDate: '2024-02-15',
    amount: 99.00,
    features: [
      '10,000 messages/month',
      'Unlimited templates',
      'Advanced analytics',
      'Priority support',
      'API access'
    ]
  });

  // Mock payment history
  const [paymentHistory] = useState([
    {
      id: 1,
      date: '2024-01-15',
      amount: 99.00,
      status: 'Paid',
      invoice: 'INV-2024-001',
      method: 'Credit Card •••• 4242'
    },
    {
      id: 2,
      date: '2023-12-15',
      amount: 99.00,
      status: 'Paid',
      invoice: 'INV-2023-012',
      method: 'Credit Card •••• 4242'
    },
    {
      id: 3,
      date: '2023-11-15',
      amount: 99.00,
      status: 'Paid',
      invoice: 'INV-2023-011',
      method: 'Credit Card •••• 4242'
    },
    {
      id: 4,
      date: '2023-10-15',
      amount: 79.00,
      status: 'Paid',
      invoice: 'INV-2023-010',
      method: 'Credit Card •••• 4242'
    }
  ]);

  // Mock invoices
  const [invoices] = useState([
    {
      id: 1,
      invoiceNumber: 'INV-2024-001',
      date: '2024-01-15',
      amount: 99.00,
      status: 'Paid',
      downloadUrl: '#'
    },
    {
      id: 2,
      invoiceNumber: 'INV-2023-012',
      date: '2023-12-15',
      amount: 99.00,
      status: 'Paid',
      downloadUrl: '#'
    },
    {
      id: 3,
      invoiceNumber: 'INV-2023-011',
      date: '2023-11-15',
      amount: 99.00,
      status: 'Paid',
      downloadUrl: '#'
    }
  ]);

  // Mock payment methods
  const [paymentMethods] = useState([
    {
      id: 1,
      type: 'card',
      last4: '4242',
      brand: 'Visa',
      expiryMonth: 12,
      expiryYear: 2025,
      isDefault: true
    },
    {
      id: 2,
      type: 'card',
      last4: '8888',
      brand: 'Mastercard',
      expiryMonth: 6,
      expiryYear: 2026,
      isDefault: false
    }
  ]);

  // Available plans
  const plans = [
    {
      name: 'Starter',
      price: 29,
      billing: 'monthly',
      features: [
        '1,000 messages/month',
        'Basic templates',
        'Standard analytics',
        'Email support'
      ],
      popular: false
    },
    {
      name: 'Professional',
      price: 99,
      billing: 'monthly',
      features: [
        '10,000 messages/month',
        'Unlimited templates',
        'Advanced analytics',
        'Priority support',
        'API access'
      ],
      popular: true,
      current: subscription.plan === 'Professional'
    },
    {
      name: 'Enterprise',
      price: 299,
      billing: 'monthly',
      features: [
        'Unlimited messages',
        'Unlimited templates',
        'Custom analytics',
        '24/7 dedicated support',
        'Full API access',
        'Custom integrations',
        'SLA guarantee'
      ],
      popular: false
    }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="billing-container">
      <div className="billing-header">
        <div>
          <h1>Billing & Subscription</h1>
          <p>Manage your subscription, payment methods, and billing history</p>
        </div>
      </div>

      <div className="billing-tabs">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab-btn ${activeTab === 'plans' ? 'active' : ''}`}
          onClick={() => setActiveTab('plans')}
        >
          Plans
        </button>
        <button
          className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Payment History
        </button>
        <button
          className={`tab-btn ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          Invoices
        </button>
        <button
          className={`tab-btn ${activeTab === 'methods' ? 'active' : ''}`}
          onClick={() => setActiveTab('methods')}
        >
          Payment Methods
        </button>
      </div>

      {activeTab === 'overview' && (
        <div className="billing-content">
          <div className="subscription-card">
            <div className="subscription-header">
              <div>
                <h2>Current Plan</h2>
                <p className="subscription-status">
                  <span className={`status-badge ${subscription.status.toLowerCase()}`}>
                    {subscription.status}
                  </span>
                </p>
              </div>
              <button className="btn-upgrade" onClick={() => setActiveTab('plans')}>
                Change Plan
              </button>
            </div>
            <div className="subscription-details">
              <div className="plan-info">
                <div className="plan-name">{subscription.plan}</div>
                <div className="plan-price">
                  {formatCurrency(subscription.amount)}/{subscription.billingCycle === 'Monthly' ? 'mo' : 'yr'}
                </div>
              </div>
              <div className="billing-info">
                <div className="info-item">
                  <span className="info-label">Billing Cycle:</span>
                  <span className="info-value">{subscription.billingCycle}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Next Billing Date:</span>
                  <span className="info-value">{formatDate(subscription.nextBillingDate)}</span>
                </div>
              </div>
              <div className="plan-features">
                <h3>Plan Features</h3>
                <ul>
                  {subscription.features.map((feature, index) => (
                    <li key={index}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="billing-summary-grid">
            <div className="summary-card">
              <div className="summary-icon" style={{ background: '#d1fae5', color: '#10b981' }}>
                💳
              </div>
              <div className="summary-content">
                <div className="summary-value">{formatCurrency(subscription.amount)}</div>
                <div className="summary-label">Monthly Cost</div>
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-icon" style={{ background: '#dbeafe', color: '#3b82f6' }}>
                📊
              </div>
              <div className="summary-content">
                <div className="summary-value">{paymentHistory.length}</div>
                <div className="summary-label">Total Payments</div>
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-icon" style={{ background: '#fef3c7', color: '#f59e0b' }}>
                📄
              </div>
              <div className="summary-content">
                <div className="summary-value">{invoices.length}</div>
                <div className="summary-label">Invoices</div>
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-icon" style={{ background: '#e0e7ff', color: '#667eea' }}>
                🔒
              </div>
              <div className="summary-content">
                <div className="summary-value">{paymentMethods.length}</div>
                <div className="summary-label">Payment Methods</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'plans' && (
        <div className="billing-content">
          <div className="plans-header">
            <h2>Choose Your Plan</h2>
            <p>Select the plan that best fits your business needs</p>
          </div>
          <div className="plans-grid">
            {plans.map((plan, index) => (
              <div key={index} className={`plan-card ${plan.popular ? 'popular' : ''} ${plan.current ? 'current' : ''}`}>
                {plan.popular && <div className="popular-badge">Most Popular</div>}
                {plan.current && <div className="current-badge">Current Plan</div>}
                <div className="plan-card-header">
                  <h3>{plan.name}</h3>
                  <div className="plan-card-price">
                    <span className="price-amount">{formatCurrency(plan.price)}</span>
                    <span className="price-period">/{plan.billing}</span>
                  </div>
                </div>
                <ul className="plan-features-list">
                  {plan.features.map((feature, idx) => (
                    <li key={idx}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  className={`btn-plan ${plan.current ? 'btn-current' : plan.popular ? 'btn-primary' : 'btn-secondary'}`}
                  disabled={plan.current}
                >
                  {plan.current ? 'Current Plan' : 'Select Plan'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="billing-content">
          <div className="section-header">
            <h2>Payment History</h2>
            <p>View all your past transactions</p>
          </div>
          <div className="payment-history-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Invoice</th>
                  <th>Payment Method</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map((payment) => (
                  <tr key={payment.id}>
                    <td>{formatDate(payment.date)}</td>
                    <td className="amount-cell">{formatCurrency(payment.amount)}</td>
                    <td>
                      <span className={`status-badge ${payment.status.toLowerCase()}`}>
                        {payment.status}
                      </span>
                    </td>
                    <td>{payment.invoice}</td>
                    <td>{payment.method}</td>
                    <td>
                      <button className="btn-link">View Details</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'invoices' && (
        <div className="billing-content">
          <div className="section-header">
            <h2>Invoices</h2>
            <p>Download and view your invoices</p>
          </div>
          <div className="invoices-list">
            {invoices.map((invoice) => (
              <div key={invoice.id} className="invoice-card">
                <div className="invoice-info">
                  <div className="invoice-number">{invoice.invoiceNumber}</div>
                  <div className="invoice-date">{formatDate(invoice.date)}</div>
                </div>
                <div className="invoice-amount">{formatCurrency(invoice.amount)}</div>
                <div className="invoice-status">
                  <span className={`status-badge ${invoice.status.toLowerCase()}`}>
                    {invoice.status}
                  </span>
                </div>
                <button className="btn-download">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  Download
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'methods' && (
        <div className="billing-content">
          <div className="section-header">
            <h2>Payment Methods</h2>
            <button className="btn-add-method">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add Payment Method
            </button>
          </div>
          <div className="payment-methods-list">
            {paymentMethods.map((method) => (
              <div key={method.id} className="payment-method-card">
                <div className="method-icon">
                  {method.type === 'card' && (
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                      <line x1="1" y1="10" x2="23" y2="10"></line>
                    </svg>
                  )}
                </div>
                <div className="method-details">
                  <div className="method-name">
                    {method.brand} •••• {method.last4}
                  </div>
                  <div className="method-expiry">
                    Expires {method.expiryMonth}/{method.expiryYear}
                  </div>
                  {method.isDefault && (
                    <span className="default-badge">Default</span>
                  )}
                </div>
                <div className="method-actions">
                  {!method.isDefault && (
                    <button className="btn-link">Set as Default</button>
                  )}
                  <button className="btn-link text-danger">Remove</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;

